import { useEffect, useRef } from 'react'
import type React from 'react'
import { useBoolean, useLatest } from 'react-use'

import { getWindow } from '@ztechapps/common.utils'

export type DragResizePointer = { startX: number; startY: number; clientX: number; clientY: number }

export type UseDragResizeProps = {
  cursor?: string
  onStart?: () => void
  onDrag?: (pointer: DragResizePointer) => void
}

export type UseDragResizeResult = {
  isResizing: boolean
  onMouseDown: (event: React.MouseEvent<HTMLElement>) => void
}

/**
 * `useDragResize` — shared pointer-drag resizing primitive.
 *
 * The caller owns what a drag means — it measures its own origin in
 * `onStart` and turns the pointer into a size in `onDrag`.
 */
export const useDragResize = ({ cursor = '', onStart, onDrag }: UseDragResizeProps = {}): UseDragResizeResult => {
  const [isResizing, setIsResizing] = useBoolean(false)
  const onDragRef = useLatest(onDrag)
  const startRef = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const doc = getWindow()?.document
    if (!doc || !isResizing) return

    const { x: startX, y: startY } = startRef.current

    const handleMouseMove = (e: MouseEvent) => {
      onDragRef.current?.({ startX, startY, clientX: e.clientX, clientY: e.clientY })
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    doc.body.style.cursor = cursor
    doc.body.style.userSelect = 'none'
    doc.addEventListener('mousemove', handleMouseMove)
    doc.addEventListener('mouseup', handleMouseUp)

    return () => {
      doc.removeEventListener('mousemove', handleMouseMove)
      doc.removeEventListener('mouseup', handleMouseUp)
      doc.body.style.cursor = ''
      doc.body.style.userSelect = ''
    }
  }, [isResizing, cursor, onDragRef, setIsResizing])

  const onMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    startRef.current = { x: event.clientX, y: event.clientY }
    onStart?.()
    setIsResizing(true)
  }

  return { isResizing, onMouseDown }
}
